<template>
  <div id="pageItemList">
    <v-dialog
      v-model="value.show"
      @input="v => v || closeAction()"
      max-width="500px"
      :persistent="true"
    >
      <v-card>
        <v-card-title>
          <span class="headline">{{
            value.mode === "edit" ? $t("editUser") : $t("createUser")
          }}</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="editCreateForm">
            <v-text-field
              name="name"
              :label="$t('name')"
              type="text"
              v-model="user.name"
              v-validate="'required|max:255'"
              :data-vv-as="$t('name')"
              :error-messages="errors.collect('name')"
            ></v-text-field>
            <v-text-field
              name="username"
              :label="$t('username')"
              type="text"
              v-model="user.username"
              v-validate="'required|max:180'"
              :data-vv-as="$t('username')"
              :error-messages="errors.collect('username')"
            ></v-text-field>
            <v-flex xs12>
              <v-select
                v-model="user.roles"
                :items="allPermissions"
                :label="$t('permissions')"
                prepend-icon="lock"
                multiple
                chips
              ></v-select>
            </v-flex>
            <v-flex xs12>
              <v-radio-group v-model="userEnabledRadio" row>
                <v-radio :label="$t('enabled')" value="1"></v-radio>
                <v-radio :label="$t('enabledUntil')" value="2"></v-radio>
                <v-radio :label="$t('disabled')" value="3"></v-radio>
              </v-radio-group>
            </v-flex>
            <v-flex xs12>
              <v-text-field
                name="email"
                :label="$t('email')"
                type="text"
                v-model="user.email"
                v-validate="
                  user.send_new_issue_reminder_after &&
                  user.send_new_issue_reminder_after.length > 0
                    ? 'required|email'
                    : 'email'
                "
                :data-vv-as="$t('email')"
                :error-messages="errors.collect('email')"
              ></v-text-field>
              <v-autocomplete
                name="send_new_issue_reminder_after"
                :label="$t('sendNewIssueReminderAfter')"
                :items="hourPeriods"
                v-model="user.send_new_issue_reminder_after"
                clearable
                chips
                :data-vv-as="$t('sendNewIssueReminderAfter')"
                :error-messages="
                  errors.collect('send_new_issue_reminder_after')
                "
                multiple
              ></v-autocomplete>
            </v-flex>
            <v-flex xs12 v-if="userEnabledRadio === '2'">
              <v-menu
                :close-on-content-click="false"
                v-model="menu"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
              >
                <v-text-field
                  name="enabled_until"
                  slot="activator"
                  :value="
                    user.enabled_until
                      ? $moment(user.enabled_until).format('LL')
                      : null
                  "
                  v-validate="userEnabledRadio === '2' ? 'required' : ''"
                  :label="$t('enabledUntil')"
                  :data-vv-as="$t('enabledUntil')"
                  :error-messages="errors.collect('enabled_until')"
                  readonly
                ></v-text-field>
                <v-date-picker
                  v-model="user.enabled_until"
                  @input="menu = false"
                  :locale="LANG"
                  reactive
                ></v-date-picker>
              </v-menu>
            </v-flex>
            <v-subheader class="pa-0 mt-3">{{
              value.mode === "edit" ? $t("changePassword") : $t("password")
            }}</v-subheader>
            <v-text-field
              name="password"
              :label="$t('password')"
              type="password"
              ref="passwordRef"
              v-model="user.password"
              v-validate="value.mode === 'create' ? 'required' : ''"
              :data-vv-as="$t('password')"
              :error-messages="errors.collect('password')"
              autocomplete="new-password"
            ></v-text-field>
            <v-text-field
              name="passwordRepeat"
              :label="$t('passwordRepeat')"
              type="password"
              v-model="passwordRepeat"
              v-validate="
                value.mode === 'create'
                  ? 'required|confirmed:passwordRef'
                  : 'confirmed:passwordRef'
              "
              :data-vv-as="$t('passwordRepeat')"
              :error-messages="errors.collect('passwordRepeat')"
              autocomplete="new-password"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="blue darken-1"
            flat
            @click.native="closeAction"
            :disabled="formDisabled"
            >{{ $t("close") }}</v-btn
          >
          <v-btn
            color="blue darken-1"
            flat
            @click.native="saveAction"
            :disabled="formDisabled"
            >{{ $t("save") }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  UPDATE_USER,
  CREATE_USER,
  RESET_USER,
  SHOW_SNACKBAR
} from "@/store/actions.type";
import validationErrorsMixin from "@/mixins/validationErrors.mixin";

export default {
  props: ["value"],
  data() {
    return {
      formDisabled: false,
      passwordRepeat: "",
      menu: false,
      LANG: process.env.VUE_APP_LANG,
      userEnabledRadio: "1"
    };
  },
  computed: {
    ...mapGetters(["user", "allPermissions"]),
    hourPeriods: function() {
      let hours = [];
      for (let i = 1; i <= 24; i++) {
        hours.push(i + "h");
      }

      return hours;
    }
  },
  mixins: [validationErrorsMixin],
  watch: {
    value: {
      handler: function() {
        if (this.value.mode === "edit" && this.value.show === true) {
          if (this.user.enabled_until && this.user.enabled) {
            this.userEnabledRadio = "2";
          } else if (!this.user.enabled) {
            this.userEnabledRadio = "3";
          } else {
            this.userEnabledRadio = "1";
          }
        }
      },
      deep: true
    }
  },
  methods: {
    saveAction() {
      this.$validator.validateAll().then(result => {
        if (result) {
          this.formDisabled = true;

          switch (this.userEnabledRadio) {
            case "1":
            case "2":
              this.user.enabled = true;
              break;
            case "3":
              this.user.enabled = false;
              break;
          }
          this.$store
            .dispatch(
              this.value.mode === "edit" ? UPDATE_USER : CREATE_USER,
              this.user
            )
            .then(() => {
              this.$store.dispatch(SHOW_SNACKBAR, {
                text: this.$t("savedSuccessfully", [this.$t("user")]),
                color: "success"
              });

              this.closeAction();
              this.$emit("saved");
            })
            .catch(() => {
              this.formDisabled = false;
            });
        }
      });
    },
    closeAction() {
      this.$emit("input", {
        mode: this.value.mode,
        show: false
      });
      this.formDisabled = false;
      // we need wait for animation until is finished
      setTimeout(() => {
        this.$store.dispatch(RESET_USER);
        this.$refs.editCreateForm.reset();
        this.$validator.reset();
      }, 100);
    }
  }
};
</script>

<style scoped></style>
